import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useGetBundle } from '~/hooks/apis/accommodation/useBundles';
import useAppNavigation from '~/hooks/useAppNavigation';

import AccommodationBundleContentTab from './tabs/AccommodationBundleContentTab';
import AccommodationBundleExperiencesTab from './tabs/AccommodationBundleExperiencesTab';
import AccommodationBundleHotelsTab from './tabs/AccommodationBundleHotelsTab';
import AccommodationBundleRuleTab from './tabs/AccommodationBundleRuleTab';
import AccommodationBundleSchedulesTab from './tabs/AccommodationBundleSchedulesTab';

const TAB_VALUES = ['experiences', 'hotels', 'rules', 'schedules', 'content'] as const;
type TabValue = (typeof TAB_VALUES)[number];

function AccommodationBundleEditPage() {
  const { bundleId, tab } = useParams<{ bundleId: string; tab?: string }>();
  const { replace } = useAppNavigation();
  const { data: bundle, isLoading, error } = useGetBundle({ bundleId });

  useEffect(() => {
    if (!tab && bundleId) {
      replace(`/accommodation/bundles/${bundleId}/experiences`);
    }
  }, [tab, bundleId, replace]);

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">Failed to load bundle: {error.message}</Alert>
      </Container>
    );
  }

  if (!bundle) {
    return (
      <Container maxWidth="lg">
        <Alert severity="warning">Bundle not found.</Alert>
      </Container>
    );
  }

  const activeTab = (TAB_VALUES.includes(tab as TabValue) ? tab : 'experiences') as TabValue;

  return (
    <Container maxWidth="lg">
      <Helmet>
        <title>Edit Bundle: {bundle.name}</title>
      </Helmet>

      <PageHeader title={`Edit Bundle: ${bundle.name}`} backButton="/accommodation/properties#bundles" />

      <Box mt={3} border={1} borderColor="divider" borderRadius={1}>
        <Tabs value={activeTab} variant="scrollable">
          <Tab
            value="experiences"
            label={`Experiences (${bundle.offers?.filter((o) => o.entityType === 'EXPERIENCE').length ?? 0})`}
            to={`/accommodation/bundles/${bundleId}/experiences`}
            component={Link}
          />
          <Tab
            value="hotels"
            label={`Hotels (${bundle.offers?.filter((o) => o.entityType === 'HOTEL').length ?? 0})`}
            to={`/accommodation/bundles/${bundleId}/hotels`}
            component={Link}
          />
          <Tab value="rules" label="Rules" to={`/accommodation/bundles/${bundleId}/rules`} component={Link} />
          <Tab
            value="schedules"
            label={`Schedules (${bundle.schedules?.length ?? 0})`}
            to={`/accommodation/bundles/${bundleId}/schedules`}
            component={Link}
          />
          <Tab value="content" label="Content" to={`/accommodation/bundles/${bundleId}/content`} component={Link} />
        </Tabs>

        <Box borderTop={1} borderColor="divider" p={2}>
          {/* Per-tab keys derived from the slice of bundle data each tab edits. After a mutation,
              the bundle query is invalidated and refetched; the new content produces a new key,
              forcing the tab to remount and re-initialise its local form state from fresh data. */}
          {activeTab === 'experiences' && <AccommodationBundleExperiencesTab bundle={bundle} />}
          {activeTab === 'hotels' && (
            <AccommodationBundleHotelsTab
              key={JSON.stringify(
                (bundle.offers ?? [])
                  .filter((o) => o.entityType === 'HOTEL')
                  .map((o) => o.propertyId)
                  .sort(),
              )}
              bundle={bundle}
            />
          )}
          {activeTab === 'rules' && (
            <AccommodationBundleRuleTab key={JSON.stringify(bundle.rules ?? [])} bundle={bundle} />
          )}
          {activeTab === 'schedules' && (
            <AccommodationBundleSchedulesTab key={JSON.stringify(bundle.schedules ?? [])} bundle={bundle} />
          )}
          {activeTab === 'content' && (
            <AccommodationBundleContentTab key={`${bundle.name}::${bundle.status}`} bundle={bundle} />
          )}
        </Box>
      </Box>
    </Container>
  );
}

export default AccommodationBundleEditPage;
